import React, { Fragment, useMemo } from 'react';
import { IconDownload } from '@planne-software/uni/icons';
import { ModalHeader, Button, Modal, Tooltip } from '@planne-software/uni';

import { useTranslate } from '@src/core/hooks';
import type { Voucher } from '@src/domains/vouchers';
import useModalOverlayLock from '@src/hooks/useModalOverlayLock';

import { VoucherBlock } from './VoucherBlock';
import { useReservationVouchersModal } from './useReservationVouchersModal';
import { VoucherBlockDivider, VoucherModalBody, VoucherModalFooter, VoucherModalRoot } from '../styles';

export type ReservationVouchersModalProps = {
  open: boolean;
  title: string;
  vouchers: Voucher[];
  onClose: () => void;
  reservationId: string | null;
};

export const ReservationVouchersModal = (props: ReservationVouchersModalProps) => {
  const { open, onClose, title, vouchers, reservationId } = props;
  const { i18n } = useTranslate();

  const {
    blocks,
    showsDownloadAll,
    hasPendingVouchers,
    hasDownloadableTargets,
    singleVoucher,
    downloadAll,
    downloadOne,
    isDownloadingAll,
    isDownloadingOne,
  } = useReservationVouchersModal({ vouchers, reservationId });

  useModalOverlayLock(open);

  const isFooterDownloadDisabled = hasPendingVouchers || !hasDownloadableTargets || isDownloadingAll;

  const footerButton = useMemo(() => {
    const button = showsDownloadAll ? (
      <Button
        data-testid='voucher-download-all'
        type='button'
        variant='contained'
        loading={isDownloadingAll}
        disabled={isFooterDownloadDisabled}
        startSlot={<IconDownload size={20} aria-hidden />}
        onClick={downloadAll}
      >
        {i18n('checkout_confirmed_download_all_vouchers')}
      </Button>
    ) : (
      <Button
        data-testid='voucher-download-single'
        type='button'
        variant='contained'
        loading={isDownloadingAll}
        disabled={!singleVoucher || isFooterDownloadDisabled}
        startSlot={<IconDownload size={20} aria-hidden />}
        onClick={() => singleVoucher && downloadOne(singleVoucher)}
      >
        {i18n('checkout_vouchers_modal_download')}
      </Button>
    );

    if (!hasPendingVouchers) {
      return button;
    }

    return (
      <Tooltip arrow placement='top' label={i18n('checkout_confirmed_vouchers_pending_tooltip')}>
        <span>{button}</span>
      </Tooltip>
    );
  }, [
    showsDownloadAll,
    hasPendingVouchers,
    isFooterDownloadDisabled,
    isDownloadingAll,
    singleVoucher,
    downloadAll,
    downloadOne,
    i18n,
  ]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      disablePortal
      sx={{
        maxWidth: {
          xs: '100%',
          sm: '42rem',
        },
        margin: '0 auto',
      }}
    >
      <VoucherModalRoot data-testid='reservation-vouchers-modal'>
        <ModalHeader title={title} onClose={onClose} divider />
        <VoucherModalBody>
          {blocks.map((block, index) => (
            <Fragment key={block.key}>
              {index > 0 ? <VoucherBlockDivider aria-hidden /> : null}
              <VoucherBlock
                block={block}
                showsDownload={showsDownloadAll}
                loading={isDownloadingOne(block.voucher)}
                onDownload={() => downloadOne(block.voucher)}
              />
            </Fragment>
          ))}
        </VoucherModalBody>

        <VoucherModalFooter>{footerButton}</VoucherModalFooter>
      </VoucherModalRoot>
    </Modal>
  );
};

export default ReservationVouchersModal;
